import { isAxiosError } from 'axios';
import type {
  IAnnotationData,
  IAnnotationPhotoData,
  ICreateAnnotationRequest,
  IModifyAnnotationRequest,
  IRemoveAnnotationRequest,
  ICreateProjectRequest,
  IModifyProjectRequest,
  ICreateWorkRequest,
  IProjectData,
  IPlanData,
  IWorkData,
  ISourceData,
  IModifyWorkRequest,
  IRemoveWorkRequest,
  IReadWorkPageRequest,
  IReadProjectPageRequest,
  IReadSourcePageRequest,
  IUploadSourceRequest,
  ICreatePlanRequest,
  IUploadPlanRequest,
  IRemovePlanRequest,
  IModifyPlanRequest,
} from '@datatype/gridgio/api-type';
import * as api from '@api/gridgio/api-function';
import { queryClient } from '@config/query-client';
import { IApiResponse } from '@datatype/common/api-type';
import {
  appendQueryDataItem,
  mergeInfiniteQueryDataItem,
  snapshotQueryData,
  rollbackQueryData,
  removeQueryDataItem,
  replaceQueryDataItem,
  replaceInfiniteQueryDataItem,
  appendInfiniteQueryDataItem,
  removeInfiniteQueryDataItem,
  useApiInfiniteQuery,
  useApiMutation,
  useApiQuery,
} from '@hook/common/useApi';

const PROJECT_QUERY_KEY = 'gridgio-project';
const PROJECT_PER_PAGE = 12;
const WORK_QUERY_KEY = 'gridgio-work';
const WORK_PER_PAGE = 12;
const SOURCE_QUERY_KEY = 'gridgio-source';
const SOURCE_PER_PAGE = 12;

export const useProjectPageQuery = (page: number, perPage: number) =>
  useApiQuery([PROJECT_QUERY_KEY, page, perPage], api.readProjectPage, { page, perPage });

export const useProjectQuery = (projectKey: string) =>
  useApiQuery([PROJECT_QUERY_KEY, projectKey], api.readProject, projectKey, { enabled: !!projectKey });

export const useCreateProjectMutation = () =>
  useApiMutation<IApiResponse<IProjectData>, ICreateProjectRequest>(api.createProject, {
    onSuccess: (data) => {
      const itemKey = data.data.key;
      const createdItem = data.data;
      return appendInfiniteQueryDataItem([PROJECT_QUERY_KEY, 'infinite', PROJECT_PER_PAGE], itemKey, createdItem);
    },
  });

export const useBookmarkProjectInfiniteQuery = (perPage = PROJECT_PER_PAGE) =>
  useApiInfiniteQuery<IProjectData, IReadProjectPageRequest>(
    [PROJECT_QUERY_KEY, 'bookmark', 'infinite', perPage],
    api.readBookmarkProjectPage,
    { perPage },
  );

export const useModifyProjectMutation = () =>
  useApiMutation<
    IApiResponse<IProjectData>,
    IModifyProjectRequest,
    { snapshots: ReturnType<typeof snapshotQueryData> }
  >(api.modifyProject, {
    onMutate: async (variables) => {
      if (variables.isBookmark == null) return { snapshots: [] };
      const queryKey = [PROJECT_QUERY_KEY, 'infinite', PROJECT_PER_PAGE];
      await queryClient.cancelQueries({ queryKey, exact: true });
      const snapshots = snapshotQueryData([queryKey]);
      mergeInfiniteQueryDataItem<IProjectData>(queryKey, variables.projectKey, { isBookmark: variables.isBookmark });
      return { snapshots };
    },
    onError: (_error, _variables, context) => {
      if (context?.snapshots) rollbackQueryData(context.snapshots);
    },
    onSuccess: (data, variables) => {
      const itemKey = data.data.key;
      const updatedItem = data.data;
      replaceQueryDataItem([PROJECT_QUERY_KEY, itemKey], undefined, itemKey, updatedItem);
      replaceInfiniteQueryDataItem([PROJECT_QUERY_KEY, 'infinite', PROJECT_PER_PAGE], itemKey, updatedItem);

      const bookmarkQueryKey = [PROJECT_QUERY_KEY, 'bookmark', 'infinite', PROJECT_PER_PAGE];
      if (variables.isBookmark === true) {
        return appendInfiniteQueryDataItem(bookmarkQueryKey, itemKey, updatedItem);
      } else if (variables.isBookmark === false) {
        return removeInfiniteQueryDataItem(bookmarkQueryKey, itemKey);
      } else {
        replaceInfiniteQueryDataItem(bookmarkQueryKey, itemKey, updatedItem);
      }
    },
  });

export const useRemoveProjectMutation = () =>
  useApiMutation<IApiResponse<void>, string>(api.removeProject, {
    onSuccess: (_data, itemKey) => {
      void removeInfiniteQueryDataItem([PROJECT_QUERY_KEY, 'infinite', PROJECT_PER_PAGE], itemKey);
      void removeInfiniteQueryDataItem([PROJECT_QUERY_KEY, 'bookmark', 'infinite', PROJECT_PER_PAGE], itemKey);
    },
  });

export const useWorkPageQuery = (projectKey: string, page: number, perPage: number) =>
  useApiQuery<IApiResponse<IWorkData[]>, IReadWorkPageRequest>(
    [WORK_QUERY_KEY, projectKey, page, perPage],
    api.readWorkPage,
    { projectKey, page, perPage },
    { enabled: !!projectKey },
  );

export const useMyWorkPageQuery = (page: number, perPage: number) =>
  useApiQuery([WORK_QUERY_KEY, 'me', page, perPage], api.readMyWorkPage, {
    page,
    perPage,
    sortBy: 'updatedAt',
    sortOrder: 'DESC',
  });

export const useWorkQuery = (workKey: string) =>
  useApiQuery([WORK_QUERY_KEY, workKey], api.readWork, workKey, { enabled: !!workKey });

export const useCreateWorkMutation = () =>
  useApiMutation<IApiResponse<IWorkData>, ICreateWorkRequest>(api.createWork, {
    onSuccess: (data, variables) => {
      const itemKey = data.data.key;
      const createdItem = data.data;
      return appendInfiniteQueryDataItem(
        [WORK_QUERY_KEY, variables.projectKey, 'infinite', WORK_PER_PAGE],
        itemKey,
        createdItem,
      );
    },
  });

export const useModifyWorkMutation = () =>
  useApiMutation<IApiResponse<IWorkData>, IModifyWorkRequest>(api.modifyWork, {
    onSuccess: (data) => {
      const itemKey = data.data.key;
      const updatedItem = data.data;
      replaceQueryDataItem([WORK_QUERY_KEY, itemKey], undefined, itemKey, updatedItem);

      replaceInfiniteQueryDataItem(
        [WORK_QUERY_KEY, updatedItem.project.key, 'infinite', WORK_PER_PAGE],
        itemKey,
        updatedItem,
      );
    },
  });

export const useRemoveWorkMutation = () =>
  useApiMutation<IApiResponse<void>, IRemoveWorkRequest>(api.removeWork, {
    onSuccess: (_data, variables) => {
      return removeInfiniteQueryDataItem(
        [WORK_QUERY_KEY, variables.projectKey, 'infinite', WORK_PER_PAGE],
        variables.workKey,
      );
    },
  });

export const useUploadSourceMutation = () =>
  useApiMutation<IApiResponse<ISourceData>, IUploadSourceRequest>(api.uploadSource, {
    onSuccess: (data) => {
      const itemKey = data.data.key;
      const createdItem = data.data;
      return appendInfiniteQueryDataItem(
        [SOURCE_QUERY_KEY, 'infinite', SOURCE_PER_PAGE, null],
        itemKey,
        createdItem,
        SOURCE_PER_PAGE,
      );
    },
  });

export const useSourcePageQuery = (page: number, perPage: number, projectKey?: string) =>
  useApiQuery([SOURCE_QUERY_KEY, page, perPage, projectKey ?? null], api.readSourcePage, {
    page,
    perPage,
    projectKey,
  });

export const useSourceInfiniteQuery = (perPage = SOURCE_PER_PAGE, projectKey?: string) =>
  useApiInfiniteQuery<ISourceData, IReadSourcePageRequest>(
    [SOURCE_QUERY_KEY, 'infinite', perPage, projectKey ?? null],
    api.readSourcePage,
    { perPage, projectKey },
  );

export const useProjectInfiniteQuery = (perPage = PROJECT_PER_PAGE) =>
  useApiInfiniteQuery<IProjectData, IReadProjectPageRequest>(
    [PROJECT_QUERY_KEY, 'infinite', perPage],
    api.readProjectPage,
    { perPage },
  );

export const useWorkInfiniteQuery = (projectKey: string, perPage = WORK_PER_PAGE) =>
  useApiInfiniteQuery<IWorkData, IReadWorkPageRequest>(
    [WORK_QUERY_KEY, projectKey, 'infinite', perPage],
    api.readWorkPage,
    { projectKey, perPage },
    { enabled: !!projectKey },
  );

const ANNOTATION_QUERY_KEY = 'gridgio-annotation';
const PHOTO_QUERY_KEY = 'gridgio-annotation-photo';

export const useAnnotationsQuery = (workKey: string) =>
  useApiQuery([ANNOTATION_QUERY_KEY, workKey], api.readAnnotations, workKey, { enabled: !!workKey });

export const useCreateAnnotationMutation = () =>
  useApiMutation<IApiResponse<IAnnotationData>, ICreateAnnotationRequest>(api.createAnnotation, {
    onSuccess: (data, variables) => {
      const itemKey = data.data.key;
      const createdItem = data.data;
      appendQueryDataItem([ANNOTATION_QUERY_KEY, variables.workKey], undefined, itemKey, createdItem, 'end');
    },
  });

export const useModifyAnnotationMutation = () =>
  useApiMutation<IApiResponse<IAnnotationData>, IModifyAnnotationRequest>(api.modifyAnnotation, {
    onSuccess: (data, variables) => {
      const itemKey = data.data.key;
      const updatedItem = data.data;
      replaceQueryDataItem([ANNOTATION_QUERY_KEY, variables.workKey], undefined, itemKey, updatedItem);
    },
  });

export const useRemoveAnnotationMutation = () =>
  useApiMutation<IApiResponse<IAnnotationData>, IRemoveAnnotationRequest>(api.removeAnnotation, {
    onSuccess: (_data, variables) => {
      removeQueryDataItem([ANNOTATION_QUERY_KEY, variables.workKey], undefined, variables.annotationKey);
    },
  });

export const useAnnotationPhotosQuery = (annotationKey: string) =>
  useApiQuery([PHOTO_QUERY_KEY, annotationKey], api.readAnnotationPhotos, annotationKey, {
    enabled: !!annotationKey,
  });

export const useCheckAnnotationPhotoUploadMutation = () =>
  useApiMutation<IApiResponse<IAnnotationPhotoData> | null, { annotationKey: string; photoKey: string }>(
    async (request) => {
      try {
        return await api.checkAnnotationPhotoUpload(request.photoKey);
      } catch (error) {
        // 재시도 확인에서 아직 업로드되지 않은 사진은 정상 업로드 단계로 이어간다.
        if (isAxiosError(error) && error.response?.data?.code === 'CLIENT_0003') return null;
        throw error;
      }
    },
    {
      onSuccess: (data, variables) => {
        if (!data) return;
        const itemKey = data.data.key;
        const createdItem = data.data;
        appendQueryDataItem([PHOTO_QUERY_KEY, variables.annotationKey], undefined, itemKey, createdItem, 'end');
      },
    },
  );

export const useRemoveAnnotationPhotoMutation = () =>
  useApiMutation<IApiResponse<IAnnotationPhotoData>, { annotationKey: string; photoKey: string }>(
    (request) => api.removeAnnotationPhoto(request.photoKey),
    {
      onSuccess: (_data, variables) => {
        removeQueryDataItem([PHOTO_QUERY_KEY, variables.annotationKey], undefined, variables.photoKey);
      },
    },
  );

export const useCreatePlanMutation = () => useApiMutation<IApiResponse<IPlanData>, ICreatePlanRequest>(api.createPlan);

export const useUploadPlanMutation = (projectKey: string) =>
  useApiMutation<IApiResponse<ISourceData>, IUploadPlanRequest>(api.uploadPlan, {
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [PROJECT_QUERY_KEY, projectKey] }),
  });

export const useRemovePlanMutation = () =>
  useApiMutation<IApiResponse<void>, IRemovePlanRequest>(api.removePlan, {
    onSuccess: (_data, variables) => {
      removeQueryDataItem([PROJECT_QUERY_KEY, variables.projectKey], 'plans', variables.planKey);
    },
  });

export const useModifyPlanMutation = () =>
  useApiMutation<IApiResponse<IPlanData>, IModifyPlanRequest>(api.modifyPlan, {
    onSuccess: (data, variables) => {
      const itemKey = data.data.key;
      const updatedItem = data.data;
      replaceQueryDataItem([PROJECT_QUERY_KEY, variables.projectKey], 'plans', itemKey, updatedItem);
    },
  });
